/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Routes } from '@administration-client/shared';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AccessTokenClaims, AuthenticationService, ClientRoleClaim, IdentityClaims } from '@common/authentication';
import {
  createEmptyStateResource,
  createErrorStateResource,
  createStateResource,
  doIfLoadingRequired,
  StateResource,
} from '@common/tech-shared';
import { SnackBarService } from '@ui';
import { BehaviorSubject, catchError, first, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { RoleLabels } from './role.model';
import { AuthClient, OTP_CREDENTIAL_TYPE, Profile, UserCredentialWrapper } from './user.model';
import { UserRepository } from './user.repository';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly userRepository: UserRepository = inject(UserRepository);
  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);
  private readonly snackBarService: SnackBarService = inject(SnackBarService);

  readonly twoFactorStateResource: BehaviorSubject<StateResource<UserCredentialWrapper>> = new BehaviorSubject(
    createEmptyStateResource<UserCredentialWrapper>(),
  );

  public getProfile(): Profile {
    const identityClaims: IdentityClaims = this.authenticationService.getIdentityClaims();
    return {
      name: this.authenticationService.getCurrentUserName(),
      initials: this.authenticationService.getCurrentUserInitials(),
      username: identityClaims.preferred_username,
      email: identityClaims.email,
      roles: this._buildRoles(this.authenticationService.getAccessTokenClaims()),
      organisationsEinheiten: identityClaims.organisationEinheitName ?? [],
    };
  }

  _buildRoles(claims: AccessTokenClaims): string[] {
    const labeledRoles: string[] = Object.values(AuthClient)
      .map((client: AuthClient) => claims.resource_access?.[client])
      .flatMap((clientRoleClaim: ClientRoleClaim) => clientRoleClaim?.roles ?? [])
      .map((role: string) => RoleLabels[role])
      .filter((label: string) => !!label);
    return [...new Set(labeledRoles)];
  }

  public getTwoFactorDevices(): Observable<StateResource<UserCredentialWrapper>> {
    return this.twoFactorStateResource
      .asObservable()
      .pipe(switchMap((stateResource: StateResource<UserCredentialWrapper>) => this._handleTwoFactorChanges(stateResource)));
  }

  _handleTwoFactorChanges(stateResource: StateResource<UserCredentialWrapper>): Observable<StateResource<UserCredentialWrapper>> {
    doIfLoadingRequired(stateResource, (): void => this._loadTwoFactorDevices());
    return this.twoFactorStateResource.asObservable();
  }

  _loadTwoFactorDevices(): void {
    this._setTwoFactorLoading();
    this.userRepository
      .getCredentialContainers()
      .pipe(first())
      .subscribe({
        next: (containers: UserCredentialWrapper[]): void => this._updateTwoFactorResource(containers),
        error: (errorResponse: HttpErrorResponse): void => this._handleTwoFactorLoadError(errorResponse),
      });
  }

  _updateTwoFactorResource(containers: UserCredentialWrapper[]): void {
    this.twoFactorStateResource.next(createStateResource(this._findTwoFactorDevices(containers)));
  }

  _findTwoFactorDevices(containers: UserCredentialWrapper[]): UserCredentialWrapper {
    return containers.find((container: UserCredentialWrapper) => container.type === OTP_CREDENTIAL_TYPE) ?? null;
  }

  _handleTwoFactorLoadError(errorResponse: HttpErrorResponse): void {
    this.twoFactorStateResource.next(createErrorStateResource(errorResponse.error));
  }

  public changePassword(): void {
    this.authenticationService.updatePassword(Routes.PROFILE);
  }

  public addTwoFactorDevice(): void {
    this.authenticationService.configureTotp(Routes.PROFILE);
  }

  public renameTwoFactorDevice(credentialId: string, label: string): Observable<StateResource<string>> {
    return this.userRepository.updateLabel(credentialId, label).pipe(
      map((): StateResource<string> => createStateResource(label)),
      tap((): void => this._handleTwoFactorDeviceRenamed()),
      catchError(
        (errorResponse: HttpErrorResponse): Observable<StateResource<string>> =>
          of(this._handleTwoFactorDeviceRenameError(errorResponse)),
      ),
      startWith(createEmptyStateResource<string>(true)),
    );
  }

  _handleTwoFactorDeviceRenamed(): void {
    this._refreshTwoFactor();
    this.snackBarService.showInfo('Der Gerätename wurde geändert.');
  }

  _handleTwoFactorDeviceRenameError(errorResponse: HttpErrorResponse): StateResource<string> {
    this.snackBarService.showError('Der Gerätename konnte nicht geändert werden.');
    return createErrorStateResource<string>(errorResponse.error);
  }

  public deleteTwoFactorDevice(credentialId: string): Observable<StateResource<string>> {
    return this.userRepository.deleteCredential(credentialId).pipe(
      map((): StateResource<string> => createStateResource(credentialId)),
      tap((): void => this._handleTwoFactorDeviceDeleted()),
      catchError(
        (errorResponse: HttpErrorResponse): Observable<StateResource<string>> =>
          of(this._handleTwoFactorDeviceDeleteError(errorResponse)),
      ),
      startWith(createEmptyStateResource<string>(true)),
    );
  }

  _handleTwoFactorDeviceDeleted(): void {
    this._refreshTwoFactor();
    this.snackBarService.showInfo('Das Gerät wurde gelöscht.');
  }

  _handleTwoFactorDeviceDeleteError(errorResponse: HttpErrorResponse): StateResource<string> {
    this.snackBarService.showError('Das Gerät konnte nicht gelöscht werden.');
    return createErrorStateResource<string>(errorResponse.error);
  }

  _setTwoFactorLoading(): void {
    this.twoFactorStateResource.next({ ...this.twoFactorStateResource.value, loading: true });
  }

  _refreshTwoFactor(): void {
    this.twoFactorStateResource.next({
      ...createEmptyStateResource<UserCredentialWrapper>(),
      reload: true,
    });
  }
}
